import store from '@/store';
import { Permission } from '@/module/configuration/navigation/types/navigation';

interface Field {
  key: string;
  permissions: string[];
}

interface DomainConfig {
  domain: string;
  permissions: string[];
  fields?: Field[] | null;
}

interface Role {
  manager?: boolean;
  domains?: DomainConfig[];
}

export default class PermissionService {
  static checkAdmin(): boolean {
    const roles = JSON.parse(localStorage.getItem('roles'));
    return roles?.some(role => role.manager === true) || false;
  }
  
  static getRoles(): Role[] {
    return JSON.parse(localStorage.getItem('roles')) || [];
  }

  static check(permissions: Permission[]): boolean {
    if (!permissions || permissions.length === 0) return false;
    if (this.checkAdmin()) return true;

    const roles = JSON.parse(localStorage.getItem('roles'));
    if (!roles) return false;

    // Verificar si el usuario tiene al menos uno de los permisos requeridos
    return permissions.some(permission => {
      // Si el dominio es 'All', permitir acceso
      if (permission.domain === 'All') return true;

      return roles.some(role => {
        const domainConfig = role.domains?.find(d => d.domain === permission.domain);
        // Verificar si el dominio existe y tiene los permisos necesarios
        if (!domainConfig) return false;
        
        return permission.permisions.some((p: string) => 
          domainConfig.permissions.includes(p)
        );
      });
    });
  }

  static hasPermission(domain: string, permission: string): boolean {
    if (this.checkAdmin()) return true;
    if (domain === 'All') return true;

    const roles = JSON.parse(localStorage.getItem('roles'));
    if (!roles) return false;

    return roles.some(role => {
      const domainConfig = role.domains?.find(d => d.domain === domain);
      return domainConfig?.permissions.includes(permission);
    });
  }

  static hasFieldPermission(domain: string, fieldKey: string, neededPermissions: string[]): boolean {
    // Admin check
    if (this.checkAdmin()) return true;
    // 'All' domain check
    if (domain === 'All') return true;

    const roles = this.getRoles();
    if (!roles || roles.length === 0) return false;

    // Check if any role has the required field permissions
    return roles.some(role => {
      const domainConfig = role.domains?.find(d => d.domain === domain);
      if (!domainConfig) return false;
      
      // Si el dominio no tiene la configuración de campos, verificamos los permisos a nivel de dominio
      if (!domainConfig.fields || domainConfig.fields.length === 0) {
        // Verifica si el dominio tiene al menos uno de los permisos necesarios
        return neededPermissions.some(perm => domainConfig.permissions.includes(perm));
      }
      
      // Buscar configuración de permisos para el campo específico
      const fieldConfig = domainConfig.fields.find(f => f.key === fieldKey);
      
      // Si el campo no está definido, usamos los permisos del dominio
      if (!fieldConfig) {
        return neededPermissions.some(perm => domainConfig.permissions.includes(perm));
      }
      
      // Verifica si el campo tiene al menos uno de los permisos necesarios
      return neededPermissions.some(perm => fieldConfig.permissions.includes(perm));
    });
  }
  
  /**
   * Verifica si un campo está deshabilitado basado en permisos
   * @param domain Dominio al que pertenece el campo
   * @param fieldKey Identificador del campo
   * @param actions Acción o acciones que se intentan realizar ('write', 'read', etc)
   * @returns true si el campo debe estar deshabilitado, false en caso contrario
   */
  static isFieldDisabled(domain: string, fieldKey: string, actions: string | string[] = 'write'): boolean {
    // Mapeo de acciones a permisos necesarios
    const permissionMap: { [key: string]: string[] } = {
      'write': ['Write', 'Update'],
      'read': ['Read'],
      'delete': ['Delete'],
      'validate': ['Validate'],
      'export': ['Export'],
      'import': ['Import'],
    };
    
    // Normalizar a array
    const actionArray = Array.isArray(actions) ? actions : [actions];
    
    // Recopilar todos los permisos necesarios para todas las acciones
    const allNeededPermissions: string[] = [];
    
    actionArray.forEach(action => {
      const permsForAction = permissionMap[action.toLowerCase()] || ['Write'];
      // Añadir solo los permisos que no están ya en el array
      permsForAction.forEach(perm => {
        if (!allNeededPermissions.includes(perm)) {
          allNeededPermissions.push(perm);
        }
      });
    });
    
    // Si tiene cualquiera de los permisos necesarios, no deshabilitar
    return !this.hasFieldPermission(domain, fieldKey, allNeededPermissions);
  }
  
  /**
   * Verifica si el usuario tiene permiso para realizar una combinación de acciones
   * @param domain Dominio del campo
   * @param fieldKey Clave del campo
   * @param actions Acción o acciones a verificar
   * @param mode Modo de verificación: 'any' (cualquier acción) o 'all' (todas las acciones)
   * @returns true si el usuario tiene los permisos requeridos
   */
  static canPerformActions(
    domain: string, 
    fieldKey: string, 
    actions: string | string[] = 'write',
    mode: 'any' | 'all' = 'any'
  ): boolean {
    // Admin check
    if (this.checkAdmin()) return true;
    
    // Normalizar a array
    const actionArray = Array.isArray(actions) ? actions : [actions];
    
    // Verificar para cada acción
    const results = actionArray.map(action => {
      const permissionMap: { [key: string]: string[] } = {
        'write': ['Write', 'Update'],
        'read': ['Read'],
        'delete': ['Delete'],
        'validate': ['Validate'],
        'export': ['Export'],
        'import': ['Import'],
      };
      
      const permsForAction = permissionMap[action.toLowerCase()] || ['Write'];
      return this.hasFieldPermission(domain, fieldKey, permsForAction);
    });
    
    // Retornar según el modo
    return mode === 'any' 
      ? results.some(result => result) // Al menos uno tiene permiso
      : results.every(result => result); // Todos tienen permiso
  }
}
